import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { BedDouble, Bath, Maximize, Search, SlidersHorizontal, X } from "lucide-react";
import { Reveal, Eyebrow } from "./motion";
import { api, fmtPrice, fmtBaths } from "../../lib/api";
import FavoriteButton from "./FavoriteButton";

function Card({ p, large }) {
  return (
    <Link to={`/property/${p.id}`} data-testid={`listing-card-${p.id}`} className="group block">
      <div className="hover-img relative">
        <img src={p.images?.[0]} alt={p.title} className={`w-full object-cover ${large ? "h-[62vh]" : "h-80"}`} />
        <div className="absolute top-4 left-4 flex gap-2">
          <span className="bg-[#FAF9F6]/95 text-[#0A1128] text-[11px] uppercase tracking-[0.16em] px-4 py-2">{p.status}</span>
          {(p.feed_status === "New" || p.feed_status === "Reduced") && (
            <span className="bg-[var(--gold)] text-[#0A1128] text-[11px] uppercase tracking-[0.16em] px-4 py-2">{p.feed_status}</span>
          )}
        </div>
        <FavoriteButton id={p.id} className="absolute top-4 right-4" />
      </div>
      <div className="flex items-start justify-between mt-6 gap-4">
        <div>
          <h3 className={`font-serif text-[#0A1128] leading-tight ${large ? "text-4xl" : "text-2xl"}`}>{p.title}</h3>
          <p className="text-[var(--slate)] text-sm mt-1">{p.location}{p.mls ? ` · MLS# ${p.mls}` : ""}</p>
        </div>
        <div className="font-serif text-2xl text-[var(--gold)] whitespace-nowrap">{fmtPrice(p.price, p.currency)}</div>
      </div>
      <div className="flex gap-6 mt-4 text-[var(--slate)] text-sm">
        <span className="flex items-center gap-2"><BedDouble size={16} /> {p.beds} Beds</span>
        <span className="flex items-center gap-2"><Bath size={16} /> {fmtBaths(p.baths)} Baths</span>
        {p.area > 0 && <span className="flex items-center gap-2"><Maximize size={16} /> {p.area?.toLocaleString()} sqft</span>}
      </div>
    </Link>
  );
}

const PRICE_OPTIONS = [
  ["", "Any"], ["1000000", "US$1M"], ["2000000", "US$2M"], ["3000000", "US$3M"],
  ["4000000", "US$4M"], ["5000000", "US$5M"], ["7000000", "US$7M"],
];

const selectClass = "bg-transparent border-b border-[#0A1128]/25 py-2.5 text-sm text-[#0A1128] focus:border-[var(--gold)] focus:outline-none w-full";

export default function Listings() {
  const [locations, setLocations] = useState([]);
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [filters, setFilters] = useState({ q: "", location: "", beds: "", min_price: "", max_price: "" });

  useEffect(() => { api.get("/locations").then((r) => setLocations(r.data)); }, []);

  const fetchList = useCallback(async () => {
    const params = {};
    Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
    const { data } = await api.get("/properties", { params });
    setList(data.filter((p) => (p.status || "").toLowerCase() !== "sold"));
  }, [filters]);

  useEffect(() => { const t = setTimeout(fetchList, 250); return () => clearTimeout(t); }, [fetchList]);

  const set = (k) => (e) => setFilters({ ...filters, [k]: e.target.value });
  const clear = () => setFilters({ q: "", location: "", beds: "", min_price: "", max_price: "" });
  const active = Object.values(filters).some(Boolean);

  const [hero, ...rest] = list;
  const grid = rest.slice(0, 5);

  return (
    <section id="listings" data-testid="listings-section" className="bg-[#E8E4D9]/40 py-24 md:py-36">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
          <div>
            <Reveal><Eyebrow>The Collection</Eyebrow></Reveal>
            <Reveal delay={0.1}><h2 className="font-serif text-4xl md:text-6xl tracking-tight text-[#0A1128] mt-5">Featured Listings</h2></Reveal>
          </div>
          <Reveal delay={0.15}>
            <button data-testid="toggle-filters" onClick={() => setOpen(!open)}
              className="flex items-center gap-2 px-6 py-3 border border-[#0A1128] text-sm uppercase tracking-[0.14em] text-[#0A1128] hover:bg-[#0A1128] hover:text-[#FAF9F6] transition-colors duration-300">
              <SlidersHorizontal size={15} /> {open ? "Hide" : "Refine"} Search
            </button>
          </Reveal>
        </div>

        {open && (
          <div data-testid="filter-bar" className="bg-white border border-[#0A1128]/12 p-6 md:p-8 mb-12 grid md:grid-cols-5 gap-5 items-end">
            <div className="md:col-span-2">
              <label className="text-xs uppercase tracking-[0.16em] text-[var(--slate)]">Search</label>
              <div className="flex items-center gap-2 border-b border-[#0A1128]/25 focus-within:border-[var(--gold)]">
                <Search size={15} className="text-[#0A1128]/50" />
                <input data-testid="filter-search" value={filters.q} onChange={set("q")} placeholder="Location, title…" className="bg-transparent py-2.5 text-sm w-full focus:outline-none" />
              </div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.16em] text-[var(--slate)]">Area</label>
              <select data-testid="filter-location" value={filters.location} onChange={set("location")} className={selectClass}>
                <option value="">All Areas</option>
                {locations.map((l) => <option key={l} value={l}>{l}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.16em] text-[var(--slate)]">Beds</label>
              <select data-testid="filter-beds" value={filters.beds} onChange={set("beds")} className={selectClass}>
                <option value="">Any</option>
                {[2, 3, 4, 5].map((b) => <option key={b} value={b}>{b}+</option>)}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs uppercase tracking-[0.16em] text-[var(--slate)]">Min</label>
                <select data-testid="filter-min" value={filters.min_price} onChange={set("min_price")} className={selectClass}>
                  {PRICE_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.16em] text-[var(--slate)]">Max</label>
                <select data-testid="filter-max" value={filters.max_price} onChange={set("max_price")} className={selectClass}>
                  {PRICE_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </div>
            </div>
            {active && (
              <button data-testid="filter-clear" onClick={clear} className="md:col-span-5 flex items-center gap-1 text-xs uppercase tracking-[0.14em] text-[var(--slate)] hover:text-[#0A1128] transition-colors">
                <X size={13} /> Clear filters
              </button>
            )}
          </div>
        )}

        {list.length === 0 ? (
          <div className="bg-white border border-dashed border-[#0A1128]/15 p-20 text-center text-[var(--slate)]" data-testid="no-results">
            No residences match your criteria. Try adjusting your search.
          </div>
        ) : (
          <>
            {hero && <Reveal className="mb-12"><Card p={hero} large /></Reveal>}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-10">
              {grid.map((p, i) => <Reveal key={p.id} delay={(i % 3) * 0.1}><Card p={p} /></Reveal>)}
            </div>
            <Reveal delay={0.1}>
              <div className="text-center mt-14">
                <Link to="/listings" data-testid="browse-all-listings"
                  className="inline-block px-10 py-4 bg-[#0A1128] text-[#FAF9F6] text-[13px] uppercase tracking-[0.14em] hover:bg-[#1a2342] transition-colors duration-300">
                  Browse All Listings
                </Link>
              </div>
            </Reveal>
          </>
        )}
      </div>
    </section>
  );
}
